import { cn, formatCurrency } from "@gymflow/helpers";
import { VoucherDTO } from "@gymflow/types";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { usePageSize, usePortalRoutes } from "apps/portal/src/hooks";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { UserMemberPage } from "apps/portal/src/views/UserMember";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { DateTime } from "luxon";
import qs from "qs";
import { useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import { Switch } from "../../atoms";
import { DotBadge } from "../../atoms/DotBadge";
import { PaginatedTable } from "../../organisms";
import { normalizePaymentStatusLabel } from "../../UserMember/UserMemberPayments/helpers";
import { SettingsContainer } from "../SettingsContainer";
import { VoucherStatusToColor } from "./constants";
import HowItWorksCard from "./HowItWorksCard";

const GiftVouchersTab = () => {
  const { t } = useTranslation();
  const voucherGroups = [
    {
      label: t("page.giftVouchers.soldGiftVouchers"),
      voucherGroupType: "SOLD_GIFT_VOUCHERS",
    },
    {
      label: t("page.giftVouchers.howItWorks"),
      voucherGroupType: "HOW_IT_WORKS",
    },
  ] as const;

  const history = useHistory();
  const location = useLocation();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const pageSize = usePageSize({
    tableContainerRef,
    rowHeight: 72,
  });

  const {
    shopSettings: { voucherSalesEnabled },
    club: { timezone, defaultCurrency },
  } = useClubSettings();

  const [currentPage, setCurrentPage] = useState(0);
  const { toast } = useContext(ToastContext);

  const { createMemberLink } = usePortalRoutes();

  const { data, isFetching, fetchNextPage, fetchPreviousPage } =
    useInfiniteQuery(
      apiV2.staff.voucher.infiniteQueries.list({
        limit: pageSize,
      }),
    );

  const { mutateAsync: enableVouchers } = useMutation(
    apiV2.staff.voucher.mutations.enableVouchers(),
  );

  const { mutateAsync: disableVouchers } = useMutation(
    apiV2.staff.voucher.mutations.disableVouchers(),
  );

  const group = useMemo(() => {
    const parts = qs.parse(location.search.slice(1));
    const urlGroup = parts?.["group"] as string | undefined;
    const allVouchers =
      data?.pages?.flatMap((page) => page?.content ?? []) ?? [];
    const hasVouchers = allVouchers.length > 0;

    if (urlGroup) return urlGroup;
    return hasVouchers ? "SOLD_GIFT_VOUCHERS" : "HOW_IT_WORKS";
  }, [location.search, data]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return DateTime.fromISO(dateString)
      .setZone(timezone)
      .toLocaleString(DateTime.DATE_MED);
  };

  const currentPageData = data?.pages[currentPage]?.content ?? [];

  const columnHelper = createColumnHelper<VoucherDTO>();

  const columns = [
    columnHelper.accessor("purchasedAt", {
      header: () => t("page.giftVouchers.vouchersColumn.purchaseDate"),
      enableSorting: false,
      cell: (column) => {
        const date = column.getValue();
        return (
          <div className="text-sm font-medium text-gray-950">
            {formatDate(date)}
          </div>
        );
      },
    }),
    columnHelper.accessor(
      (row) => {
        return {
          purchasedByFirstName: row.purchasedByFirstName,
          purchasedByLastName: row.purchasedByLastName,
        };
      },
      {
        cell: (info) => {
          const { purchasedByFirstName, purchasedByLastName } = info.getValue();
          const userMemberId = info.row.original.purchasedById;
          return (
            <a
              className="text-gray-600"
              href={createMemberLink(userMemberId, UserMemberPage.Profile)}
            >
              {purchasedByFirstName + " " + purchasedByLastName}
            </a>
          );
        },
        id: "purchasedBy",
        header: () => t("page.giftVouchers.vouchersColumn.purchasedBy"),
        enableSorting: false,
      },
    ),
    columnHelper.accessor("amount", {
      header: () => t("page.giftVouchers.vouchersColumn.amount"),
      enableSorting: false,
      cell: (column) => {
        const amount = column.getValue();
        return (
          <div className="text-sm font-bold text-gray-950">
            {formatCurrency(amount, defaultCurrency)}
          </div>
        );
      },
    }),
    columnHelper.accessor("status", {
      header: () => t("page.giftVouchers.vouchersColumn.status"),
      enableSorting: false,
      cell: (column) => {
        const status = column.getValue();
        return (
          <DotBadge intent={VoucherStatusToColor[status]}>
            {normalizePaymentStatusLabel(status)}
          </DotBadge>
        );
      },
    }),
    columnHelper.accessor("redeemedAt", {
      header: () => t("page.giftVouchers.vouchersColumn.redeemedDate"),
      enableSorting: false,
      cell: (column) => {
        const date = column?.getValue() ?? "";
        return (
          <div className="text-sm font-medium text-gray-950">
            {formatDate(date)}
          </div>
        );
      },
    }),
    columnHelper.accessor(
      (row) => {
        return {
          redeemedByFirstName: row.redeemedByFirstName,
          redeemedByLastName: row.redeemedByLastName,
        };
      },
      {
        cell: (info) => {
          const { redeemedByFirstName, redeemedByLastName } = info.getValue();
          const redeemedByName =
            !redeemedByFirstName && !redeemedByLastName
              ? "-"
              : `${redeemedByFirstName} ${redeemedByLastName}`;
          const userMemberId = info.row.original.redeemedById ?? "";
          return (
            <a
              className="text-gray-600"
              href={createMemberLink(userMemberId, UserMemberPage.Profile)}
            >
              {redeemedByName}
            </a>
          );
        },
        id: "redeemedBy",
        header: () => t("page.giftVouchers.vouchersColumn.redeemedBy"),
        enableSorting: false,
      },
    ),
  ];

  return (
    <SettingsContainer
      headerContainerClassName="lg:flex-nowrap"
      title={t("page.giftVouchers.title")}
      subTitle={t("page.giftVouchers.subTitle")}
      actions={
        <div className="flex h-fit flex-row items-center gap-x-2 rounded-2xl border border-gray-200 bg-white px-3 py-2 shadow-sm">
          <Switch
            value={voucherSalesEnabled}
            onChange={async () => {
              if (voucherSalesEnabled) {
                await disableVouchers();
                toast({
                  message: t("page.giftVouchers.notifications.disabled"),
                  type: "small",
                  intent: "error",
                });
              } else {
                await enableVouchers();
                toast({
                  message: t("page.giftVouchers.notifications.enabled"),
                  type: "small",
                  intent: "success",
                });
              }
            }}
          />
          <div className="text-nowrap">
            {t("page.giftVouchers.sellGiftVouchers")}
          </div>
        </div>
      }
    >
      <div className="flex  flex-row gap-x-2 border-b border-gray-200">
        {voucherGroups.map(({ label, voucherGroupType }) => (
          <div
            key={voucherGroupType}
            onClick={() =>
              history.push({
                search: qs.stringify({ group: voucherGroupType }),
              })
            }
            className={cn(
              "max-w-min cursor-pointer flex-nowrap whitespace-nowrap px-1 pb-3 text-sm font-semibold text-gray-500",
              {
                "text-secondary-500 border-secondary-500 border-b-2":
                  group === voucherGroupType,
              },
            )}
          >
            {label}
          </div>
        ))}
      </div>
      <div
        ref={tableContainerRef}
        className={cn({
          hidden: group !== "SOLD_GIFT_VOUCHERS",
          "flex h-full": group === "SOLD_GIFT_VOUCHERS",
        })}
      >
        <PaginatedTable
          tableProps={{
            data: currentPageData,
            columns: columns,
            isFetching,
            pageSize: pageSize,
            tableContainerRef,
          }}
          hasNextPage={!!data && !!data.pages[currentPage]?.nextPageToken}
          hasPreviousPage={!!data && currentPage > 0}
          goToNextPage={() => {
            fetchNextPage();
            setCurrentPage(currentPage + 1);
          }}
          goToPreviousPage={() => {
            fetchPreviousPage();
            setCurrentPage(currentPage - 1);
          }}
        />
      </div>
      {group === "HOW_IT_WORKS" && <HowItWorksCard />}
    </SettingsContainer>
  );
};

export default GiftVouchersTab;
